<script>
// The "add a leave" form, extracted out of AgentLeaveModal so the Agent
// Calendar's drag-select chooser can open it standalone with an agent and
// date pre-filled (docs/real-estate/be/10-api-reference.md §A9) — the rest
// of AgentLeaveModal (the list + delete) has no equivalent there, since the
// calendar shows leave as calendar events instead of a list.
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import TimeRangeFields from '../components/TimeRangeFields.vue';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { timeRangeErrorKey } from '../helpers';
import { reportInvalidForm } from '../formFeedback';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import { createLeave } from '../../../../api/realEstate/agentLeave';

function emptyForm(presetDate) {
  return {
    unavailable_date: presetDate || '',
    start_time: null,
    end_time: null,
    property_id: '',
    reason: '',
  };
}

export default {
  name: 'AgentLeaveForm',
  components: { TimeRangeFields },
  props: {
    agent: {
      type: Object,
      required: true,
    },
    presetDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      isLoadingProjects: false,
      form: emptyForm(this.presetDate),
      isSubmitting: false,
      submitted: false,
      // Bumped after a successful add so TimeRangeFields (which seeds its
      // own state once) remounts in sync with the cleared form.
      formKey: 0,
    };
  },
  computed: {
    ...mapGetters('realEstate', { projects: 'getProjectOptions' }),
    fieldErrors() {
      return {
        unavailable_date: !this.form.unavailable_date,
      };
    },
    timesErrorKey() {
      return timeRangeErrorKey(this.form);
    },
    isFormValid() {
      return (
        !Object.values(this.fieldErrors).some(Boolean) && !this.timesErrorKey
      );
    },
  },
  mounted() {
    this.loadProjects();
  },
  methods: {
    ...mapActions('realEstate', ['fetchProjectOptions']),
    // Always refreshes (a project may have been added or renamed since the
    // list was cached); the spinner only shows while there's nothing yet.
    async loadProjects() {
      this.isLoadingProjects = !this.projects.length;
      try {
        await this.fetchProjectOptions();
      } finally {
        this.isLoadingProjects = false;
      }
    },
    handleTimeRangeChange(value) {
      this.form.start_time = value.start_time;
      this.form.end_time = value.end_time;
    },
    async handleAdd() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        const { data } = await createLeave(this.agent.agent_id, {
          unavailable_date: this.form.unavailable_date,
          start_time: this.form.start_time,
          end_time: this.form.end_time,
          property_id: this.form.property_id || null,
          reason: this.form.reason.trim() || null,
        });
        useAlert(this.$t('REAL_ESTATE.AGENT_LEAVE.SUCCESS_ADDED'));
        this.form = emptyForm('');
        this.formKey += 1;
        this.submitted = false;
        this.$emit('added', data);
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.AGENT_LEAVE.ERROR_ADD')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <form class="space-y-3" novalidate @submit.prevent="handleAdd">
    <div class="flex flex-wrap items-start gap-3">
      <div>
        <input
          v-model="form.unavailable_date"
          type="date"
          class="reset-base mb-0"
          :class="[
            fieldClass,
            {
              '!border-red-500': submitted && fieldErrors.unavailable_date,
            },
          ]"
          required
        />
        <p
          v-if="submitted && fieldErrors.unavailable_date"
          class="mt-1 mb-0 text-xs text-red-500"
        >
          {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
        </p>
      </div>

      <div>
        <TimeRangeFields
          :key="formKey"
          :value="{ start_time: form.start_time, end_time: form.end_time }"
          :all-day-label="$t('REAL_ESTATE.AGENT_LEAVE.ALL_DAY_LABEL')"
          @input="handleTimeRangeChange"
        />
        <p
          v-if="submitted && timesErrorKey"
          class="mt-1 mb-0 text-xs text-red-500"
        >
          {{ $t(timesErrorKey) }}
        </p>
      </div>

      <select
        v-model="form.property_id"
        :class="selectClass"
        class="!w-44 shrink-0"
      >
        <option value="">
          {{ $t('REAL_ESTATE.AGENT_LEAVE.ALL_PROJECTS') }}
        </option>
        <option
          v-for="project in projects"
          :key="project.property_id"
          :value="project.property_id"
        >
          {{ project.name }}
        </option>
      </select>

      <input
        v-model="form.reason"
        :placeholder="$t('REAL_ESTATE.AGENT_LEAVE.FIELD_REASON_PLACEHOLDER')"
        type="text"
        class="reset-base mb-0 flex-1 min-w-[160px]"
        :class="fieldClass"
      />

      <woot-button type="submit" :is-loading="isSubmitting" class="shrink-0">
        {{ $t('REAL_ESTATE.AGENT_LEAVE.ADD_BUTTON') }}
      </woot-button>
    </div>
  </form>
</template>
